import type { AiWidgetTable, AiWidgetTableCell } from "@repo/shared/types";
import { Maximize2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./table-widget.i18n";

interface TableWidgetProps {
  table: AiWidgetTable;
}

function formatCell(c: AiWidgetTableCell, formatNumber: (v: number) => string): string {
  if (c === null || c === undefined) return "";
  if (typeof c === "number") return formatNumber(c);
  return String(c);
}

function TableBlock({
  columns,
  rows,
  containerClassName,
}: {
  columns: string[];
  rows: AiWidgetTableCell[][];
  containerClassName?: string;
}) {
  const { formatNumber } = useRegionalFormat();
  return (
    <Table variant="data" containerClassName={containerClassName} className="text-xs">
      <TableHeader className="sticky top-0 z-10">
        <TableRow>
          {columns.map((col) => (
            <TableHead key={col} className="h-8 font-medium whitespace-nowrap">
              {col}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          // Rows are positional and immutable (rendered from a parsed widget,
          // never re-ordered/inserted); index is the only stable identifier.
          // biome-ignore lint/suspicious/noArrayIndexKey: see comment above
          <TableRow key={`r${i}`}>
            {row.map((cell, j) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: positional cell
              <TableCell key={`c${j}`} className="py-1 whitespace-nowrap">
                {formatCell(cell, formatNumber)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function TableWidget({ table }: TableWidgetProps) {
  const t = useT(T);
  const [open, setOpen] = useState(false);
  const { title, columns, rows } = table;

  return (
    <div className="bg-muted/30 rounded-lg p-2 relative">
      <div className="flex items-center justify-between mb-1 pr-1">
        <p className="text-xs font-medium truncate">
          {title || `${rows.length} ${rows.length === 1 ? t.row : t.rows}`}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5 flex-shrink-0"
          onClick={() => setOpen(true)}
          tooltip={t.expandTable}
        >
          <Maximize2 className="h-3 w-3" />
        </Button>
      </div>
      <TableBlock columns={columns} rows={rows} containerClassName="max-h-[280px] overflow-auto" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{title || t.table}</DialogTitle>
            <DialogDescription className="sr-only">{t.expandedView}</DialogDescription>
          </DialogHeader>
          <TableBlock
            columns={columns}
            rows={rows}
            containerClassName="max-h-[70vh] overflow-auto"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
